import React, { useEffect, useState } from "react";
import { Table } from "antd";
import { DecimalInput, formatCurrency } from "./timelogAmountUtils";
import { tasksApis } from "../../../api/NewTasks";

// A single planned tool material with the actuals being logged against it.
// taskToolMaterialId references the planned task_tool_materials row (fixed list,
// pre-populated from the task tool). timeLogToolMaterialId is present only for
// actuals already saved on this time log (used to build the update diff).
export interface TimeLogMaterialRow {
  taskToolMaterialId: number;
  timeLogToolMaterialId?: number;
  materialId?: number;
  subMaterialId?: number;
  materialName?: string;
  subMaterialName?: string;
  plannedQuantity?: number | string | null;
  plannedRate?: number | string | null;
  workUomName?: string | null;
  actualQuantity?: number | string;
  actualRate?: number | string;
}

const num = (v: any) => (v === undefined || v === null || v === "" ? 0 : Number(v));

/**
 * Read-only list of a tool's planned split-materials with editable Actual Qty
 * and Actual Rate per row. Materials are NOT added/removed here — the list is
 * fixed by the task tool; users only log actual usage.
 */
const TimeLogToolMaterialsEditor: React.FC<{
  rows: TimeLogMaterialRow[];
  disabled?: boolean;
  materialNameMap?: Record<number, string>;
  onChange: (rows: TimeLogMaterialRow[]) => void;
}> = ({ rows, disabled, materialNameMap = {}, onChange }) => {
  // Resolve sub-material names (the planned material only carries ids). Fetch
  // the sub-material dropdown for each distinct material present in the rows.
  const [subMaterialNameMap, setSubMaterialNameMap] = useState<
    Record<number, string>
  >({});
  const distinctMaterialIdsKey = Array.from(
    new Set(
      rows
        .map((r) => r.materialId)
        .filter((id): id is number => id != null),
    ),
  )
    .sort((a, b) => a - b)
    .join(",");

  useEffect(() => {
    let active = true;
    const ids = distinctMaterialIdsKey
      ? distinctMaterialIdsKey.split(",").map(Number)
      : [];
    if (!ids.length) return;
    (async () => {
      const map: Record<number, string> = {};
      await Promise.all(
        ids.map(async (id) => {
          try {
            const res = await tasksApis.fetchMachinerySubMaterialDropDown(id);
            (res?.data || []).forEach((s: any) => {
              map[s.id ?? s.subMaterialId] =
                s.subMaterialName ?? s.name ?? s.label;
            });
          } catch {
            /* ignore */
          }
        }),
      );
      if (active) setSubMaterialNameMap(map);
    })();
    return () => {
      active = false;
    };
  }, [distinctMaterialIdsKey]);

  const setField = (
    idx: number,
    field: "actualQuantity" | "actualRate",
    value: string,
  ) => {
    onChange(
      rows.map((r, i) =>
        i === idx ? { ...r, [field]: value === "" ? undefined : value } : r,
      ),
    );
  };

  if (!rows.length) {
    return (
      <div style={{ color: "#666", padding: "12px 0" }}>
        This tool has no planned materials. Add materials on the task first.
      </div>
    );
  }

  const withUom = (qty: any, uom?: string | null) =>
    qty !== undefined && qty !== null && qty !== ""
      ? `${qty}${uom ? ` ${uom}` : ""}`
      : "-";

  return (
    <Table
      rowKey={(r) => `${r.taskToolMaterialId}`}
      dataSource={rows}
      pagination={false}
      size="small"
      bordered
      scroll={{ x: "max-content" }}
      columns={[
        {
          title: "Material",
          align: "center" as const,
          render: (_: any, r: TimeLogMaterialRow) =>
            r.materialName ||
            (r.materialId != null
              ? materialNameMap[r.materialId] || `Material #${r.materialId}`
              : "-"),
        },
        {
          title: "Sub-material",
          align: "center" as const,
          render: (_: any, r: TimeLogMaterialRow) =>
            r.subMaterialName ||
            (r.subMaterialId != null
              ? subMaterialNameMap[r.subMaterialId] || `#${r.subMaterialId}`
              : "-"),
        },
        {
          title: "Planned Qty",
          align: "center" as const,
          render: (_: any, r: TimeLogMaterialRow) =>
            withUom(r.plannedQuantity, r.workUomName),
        },
        {
          title: "Planned Rate",
          align: "center" as const,
          render: (_: any, r: TimeLogMaterialRow) =>
            r.plannedRate != null && r.plannedRate !== "" ? r.plannedRate : "-",
        },
        {
          title: "Actual Qty",
          align: "center" as const,
          width: 200,
          render: (_: any, r: TimeLogMaterialRow, idx: number) => (
            <DecimalInput
              disabled={disabled}
              // Pre-populated UOM (from the planned material) shown beside the box.
              addonAfter={r.workUomName || undefined}
              value={r.actualQuantity ?? ""}
              placeholder="Actual qty"
              onChange={(v) => setField(idx, "actualQuantity", v)}
            />
          ),
        },
        {
          title: "Actual Rate",
          align: "center" as const,
          width: 170,
          render: (_: any, r: TimeLogMaterialRow, idx: number) => (
            <DecimalInput
              disabled={disabled}
              addonBefore="₹"
              value={r.actualRate ?? ""}
              placeholder="Actual rate"
              onChange={(v) => setField(idx, "actualRate", v)}
            />
          ),
        },
        {
          title: "Total",
          align: "right" as const,
          render: (_: any, r: TimeLogMaterialRow) => {
            const t = num(r.actualQuantity) * num(r.actualRate);
            return t ? formatCurrency(t) : "";
          },
        },
      ]}
    />
  );
};

export default TimeLogToolMaterialsEditor;
